import type { GetStartedRace, PartyPrimary, RaceHitDto, RaceStage, RaceStageKind, WriteInRequest } from '@signalvote/shared';
import { assertNever } from '@signalvote/shared';
import { useRace } from '@/hooks/queries';
import type { RaceOpening } from './RaceOpening';
import { asksPrimary, campaignStages, stateOf, writtenRace } from './raceStages';
import { firstGoalsOf, reseatAll, type StageDraft } from './stageGoal';
import { VoteGoalCalculator } from './VoteGoalCalculator';

export type GoalDrafts = Partial<Record<RaceStageKind, StageDraft>>;

/**
 * What a new candidate said about their race. A picked or written-in race gets a goal, and a race block at sign-up; a state
 * alone waits for a race in it.
 */
export type RaceChoice =
  | { kind: 'InState'; state: string }
  | { kind: 'Picked'; hit: RaceHitDto; primary: PartyPrimary | null; drafts: GoalDrafts }
  | { kind: 'WrittenIn'; writeIn: WriteInRequest; drafts: GoalDrafts }
  | { kind: 'NoDistrictYet' };

declare module '@tanstack/history' {
  interface HistoryState {
    /** The race the win number page worked out, carried into sign-up. */
    raceChoice?: RaceChoice;
  }
}

/** A race with a goal to work out. */
export type GoalChoice = Extract<RaceChoice, { kind: 'Picked' | 'WrittenIn' }>;

export const hasGoal = (choice: RaceChoice | null): choice is GoalChoice => choice?.kind === 'Picked' || choice?.kind === 'WrittenIn';

/** The stages of the chosen race a goal can be worked out for, and whether its goal is ready to carry on. */
export function useRaceGoal(choice: RaceChoice | null) {
  const picked = choice?.kind === 'Picked' ? choice : null;
  const race = useRace(picked?.hit.id ?? null);
  const stages: RaceStage[] =
    picked && race.data ? campaignStages(race.data.race, picked.primary) : choice?.kind === 'WrittenIn' ? writtenRace(choice.writeIn).stages : [];
  const primaryUnanswered = !!picked && !!race.data && asksPrimary(race.data.race) && picked.primary === null;
  const ready = choice?.kind === 'WrittenIn' || (!!race.data && !primaryUnanswered);
  return { race, stages, ready };
}

/** The race block a sign-up sends: the race, its primary, and a first goal per stage worked out. */
export function signUpRaceOf(choice: RaceChoice | null, stages: RaceStage[]): GetStartedRace | null {
  if (choice?.kind === 'Picked') return { raceId: choice.hit.id, writeIn: null, primary: choice.primary, goals: firstGoalsOf(stages, choice.drafts) };
  if (choice?.kind === 'WrittenIn') return { raceId: null, writeIn: choice.writeIn, primary: null, goals: firstGoalsOf(stages, choice.drafts) };
  return null;
}

const stateOfChoice = (choice: RaceChoice | null): string | null => {
  switch (choice?.kind) {
    case 'InState':
      return choice.state;
    case 'Picked':
      return stateOf(choice.hit.id);
    case 'WrittenIn':
      return choice.writeIn.state;
    case 'NoDistrictYet':
    case undefined:
      return null;
    default:
      return assertNever(choice);
  }
};

/**
 * The race and its goal as one sentence, before there is a campaign to keep them: the state, the race and the primary are
 * the blanks it opens with. Picking the same race again keeps its goal, and so does rewriting a written-in race.
 */
export function RaceGoal({ choice, onChange }: { choice: RaceChoice | null; onChange: (choice: RaceChoice) => void }) {
  const { race, stages } = useRaceGoal(choice);
  const picked = choice?.kind === 'Picked' ? choice : null;
  const written = choice?.kind === 'WrittenIn' ? choice : null;
  const goal = picked ?? written;
  const state = stateOfChoice(choice);

  const opening: RaceOpening = {
    state,
    race: picked
      ? { name: picked.hit.name, primary: race.data && asksPrimary(race.data.race) ? { answer: picked.primary } : null }
      : written && { name: written.writeIn.name, primary: null },
    edit: {
      onState: (next) => next !== state && onChange({ kind: 'InState', state: next }),
      race: {
        currentRaceId: picked?.hit.id ?? null,
        writeIn: written?.writeIn ?? null,
        onPick: (hit) => onChange(picked?.hit.id === hit.id ? picked : { kind: 'Picked', hit, primary: null, drafts: {} }),
        // Rewriting the race keeps the numbers typed for it.
        onWriteIn: (writeIn) =>
          onChange({ kind: 'WrittenIn', writeIn, drafts: written?.writeIn.seats === writeIn.seats ? written.drafts : reseatAll(written?.drafts ?? {}) }),
        onNoDistrict: () => onChange({ kind: 'NoDistrictYet' }),
      },
      onPrimary: (primary) => picked && onChange({ ...picked, primary }),
    },
  };

  return (
    <>
      <VoteGoalCalculator
        opening={opening}
        stages={stages}
        drafts={goal?.drafts ?? {}}
        onDraft={(kind, draft) => goal && onChange({ ...goal, drafts: { ...goal.drafts, [kind]: draft } })}
        writtenIn={!!written}
      />
      {picked && race.isPending && <p className="mt-3 text-sm text-muted-foreground">Loading your race…</p>}
      {picked && race.isError && (
        <p role="alert" className="mt-3 text-sm text-destructive">
          {race.error.message}
        </p>
      )}
    </>
  );
}
